import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { fmtDateLong, fmtDateTime } from '@/lib/format-date'
import { fieldLabel, formCard, inputBase, pageWide } from '@/lib/form-styles'
import { operatorApi, type ZweiFaktorEinfuehrung } from '../api/operator.api'

/**
 * AP-38-E — **Einführung der Zwei-Faktor-Pflicht** (Entscheidung „Datum + Knopf").
 *
 * Die Frist kommt aus `TWO_FACTOR_DEADLINE` und gilt mit dem Deploy. Die Mails
 * gehen erst, wenn hier jemand den Knopf drückt — zuerst an die Administratoren
 * (sie bereiten ihre Leute vor), dann an alle, die noch nicht eingerichtet haben
 * (8.6). Dazu das Zurücksetzen für Personen, bei denen der Administrator eines
 * Mandanten es nicht darf (8.9).
 */
export default function OperatorZweiFaktorPage() {
  const { t } = useTranslation()
  const [stand, setStand] = useState<ZweiFaktorEinfuehrung | null>(null)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [email, setEmail] = useState('')

  const laden = useCallback(async () => {
    try {
      setStand(await operatorApi.zweiFaktor())
    } catch (e) {
      setMeldung({ art: 'fehler', text: apiErrorText(e, t('common.error')) })
    }
  }, [t])

  useEffect(() => {
    void laden()
  }, [laden])

  const ankuendigen = async (zielgruppe: 'ADMINS' | 'ALLE') => {
    const frage = zielgruppe === 'ADMINS' ? t('zweiFaktor.betreiber.fragenAdmins') : t('zweiFaktor.betreiber.fragenAlle')
    const knopf = zielgruppe === 'ADMINS' ? t('zweiFaktor.betreiber.ankuendigenAdmins') : t('zweiFaktor.betreiber.ankuendigenAlle')
    if (!(await bestaetige(frage, { ok: knopf }))) return
    setLaeuft(true)
    setMeldung(null)
    try {
      const { anzahl } = await operatorApi.zweiFaktorAnkuendigen(zielgruppe)
      setMeldung({ art: 'ok', text: t('zweiFaktor.betreiber.gesendet', { anzahl }) })
      await laden()
    } catch (e) {
      setMeldung({ art: 'fehler', text: apiErrorText(e, t('common.error')) })
    } finally {
      setLaeuft(false)
    }
  }

  const zuruecksetzen = async (e: React.FormEvent) => {
    e.preventDefault()
    const adresse = email.trim()
    if (!adresse) return
    if (!(await bestaetige(t('zweiFaktor.betreiber.resetFrage', { email: adresse }), { ok: t('zweiFaktor.betreiber.resetKnopf'), gefaehrlich: true }))) return
    setLaeuft(true)
    setMeldung(null)
    try {
      const { email: bestaetigt } = await operatorApi.zweiFaktorZuruecksetzen(adresse)
      setMeldung({ art: 'ok', text: t('zweiFaktor.betreiber.resetOk', { email: bestaetigt }) })
      setEmail('')
      await laden()
    } catch (err) {
      setMeldung({ art: 'fehler', text: apiErrorText(err, t('common.error')) })
    } finally {
      setLaeuft(false)
    }
  }

  const versand = (gruppe: 'ADMINS' | 'ALLE') => {
    const v = stand?.versand[gruppe]
    return v
      ? t('zweiFaktor.betreiber.versandt', { datum: fmtDateTime(v.am), anzahl: v.anzahl })
      : t('zweiFaktor.betreiber.nochNicht')
  }

  return (
    <div className={pageWide}>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <ShieldCheck size={22} /> {t('zweiFaktor.betreiber.titel')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('zweiFaktor.betreiber.untertitel')}</p>
      </div>

      {meldung && (
        <div
          className={`mb-4 rounded-md px-4 py-3 text-sm ${
            meldung.art === 'ok'
              ? 'border border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400'
              : 'border border-destructive/30 bg-destructive/10 text-destructive'
          }`}
        >
          {meldung.text}
        </div>
      )}

      {stand && (
        <div className="space-y-6">
          <div className={formCard}>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">{t('zweiFaktor.betreiber.frist')}</dt>
                <dd className="font-medium text-foreground">
                  {stand.frist ? fmtDateLong(stand.frist) : t('zweiFaktor.betreiber.keineFrist')}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('zweiFaktor.betreiber.konten')}</dt>
                <dd className="font-medium text-foreground">{t('zweiFaktor.betreiber.stand', stand.konten)}</dd>
              </div>
            </dl>
            {stand.abgeschaltet && (
              <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">{t('zweiFaktor.betreiber.abgeschaltet')}</p>
            )}
            <div className="mt-5 space-y-3">
              {(['ADMINS', 'ALLE'] as const).map((gruppe) => (
                <div key={gruppe} className="flex flex-wrap items-center gap-3">
                  <Button type="button" variant={gruppe === 'ADMINS' ? 'default' : 'outline'} disabled={laeuft || !stand.frist} onClick={() => void ankuendigen(gruppe)}>
                    {gruppe === 'ADMINS' ? t('zweiFaktor.betreiber.ankuendigenAdmins') : t('zweiFaktor.betreiber.ankuendigenAlle')}
                  </Button>
                  <span className="text-sm text-muted-foreground">{versand(gruppe)}</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={zuruecksetzen} className={formCard}>
            <h2 className="font-semibold text-foreground">{t('zweiFaktor.betreiber.reset')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('zweiFaktor.betreiber.resetHinweis')}</p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="min-w-[16rem] flex-1">
                <span className={fieldLabel}>E-Mail</span>
                <input type="email" required className={inputBase} value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <Button type="submit" variant="outline" disabled={laeuft || !email.trim()}>
                {t('zweiFaktor.betreiber.resetKnopf')}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
